import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LiveSalePopup = () => {
  const [show, setShow] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [frequency, setFrequency] = useState(30000);
  const [recentOrders, setRecentOrders] = useState([]);
  const [orderIndex, setOrderIndex] = useState(0);
  const [currentDisplay, setCurrentDisplay] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [controlsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/ui-controls`),
          axios.get(`${API}/orders/recent-public`)
        ]);
        setEnabled(controlsRes.data.liveSalePopupEnabled);
        setMessageTemplate(controlsRes.data.liveSalePopupMessage);
        setFrequency(controlsRes.data.liveSalePopupFrequency || 30000);
        setRecentOrders(ordersRes.data);
      } catch (error) {
        console.error('Error fetching popup settings:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!enabled || recentOrders.length === 0) return;

    const interval = setInterval(() => {
      const order = recentOrders[orderIndex % recentOrders.length];
      const item = order.items[0];
      setCurrentDisplay({
        name: order.firstName,
        city: order.city,
        productName: item?.name || 'a product',
        productImage: item?.image || '',
        time: order.createdAt
      });
      setShow(true);
      setOrderIndex(prev => prev + 1);
      setTimeout(() => setShow(false), 5000);
    }, frequency);

    return () => clearInterval(interval);
  }, [enabled, recentOrders, frequency, orderIndex]);

  if (!enabled || !currentDisplay) return null;

  const displayMessage = messageTemplate
    .replace('{product}', currentDisplay.productName)
    .replace('{city}', currentDisplay.city);

  const timeAgo = () => {
    if (!currentDisplay.time) return 'just now';
    const diff = (Date.now() - new Date(currentDisplay.time).getTime()) / 60000;
    if (diff < 1) return 'just now';
    if (diff < 60) return `${Math.floor(diff)} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 50, x: 50 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          className="fixed bottom-24 right-6 bg-white border-2 border-[#D4AF37] shadow-xl rounded-none p-4 w-80 z-30"
          data-testid="live-sale-popup"
        >
          <button
            onClick={() => setShow(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            data-testid="popup-close-btn"
          >
            <X size={16} />
          </button>
          <div className="flex items-start gap-3">
            {currentDisplay.productImage && (
              <img
                src={currentDisplay.productImage}
                alt={currentDisplay.productName}
                className="w-16 h-16 object-cover"
              />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0A192F]">{displayMessage}</p>
              <p className="text-xs text-gray-500 mt-1">{timeAgo()}</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
